import { Component, afterNextRender, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { CookieConsentService } from '../../services/cookie-consent.service';
import { isGaTrackedPath } from '../../services/google-analytics.service';

function currentPath(url: string): string {
  return url.split('?')[0].split('#')[0];
}

/**
 * GDPR cookie consent banner, shown once per visitor until they choose. Only
 * gates Google Analytics (see GoogleAnalyticsService) — the auth token and
 * widget visitor ID are strictly necessary and always active regardless of
 * this choice. Reopenable any time via the footer's "Cookie preferences"
 * link (CookieConsentService.reset()).
 */
@Component({
  selector: 'app-cookie-consent-banner',
  standalone: true,
  imports: [RouterLink],
  template: `
    @if (showBanner()) {
      <!-- pb inset: index.html opts into viewport-fit=cover, so without this
           the buttons sit under the iPhone home indicator. -->
      <div
        class="fixed inset-x-0 bottom-0 z-50 border-t bg-white shadow-[0_-4px_20px_rgba(0,0,0,0.08)] pb-[env(safe-area-inset-bottom)]"
        role="dialog"
        aria-live="polite"
        aria-label="Cookie consent"
      >
        <div class="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center gap-4">
          <p class="text-sm text-gray-600 flex-1">
            We use Google Analytics to understand how visitors use askvo.io. We only load it if you
            accept — see our <a routerLink="/privacy" class="text-brand-600 hover:underline">privacy policy</a>
            for details.
          </p>
          <div class="flex gap-2 shrink-0">
            <button
              type="button"
              (click)="reject()"
              class="px-4 py-2 text-sm font-medium text-gray-700 border rounded-lg hover:bg-gray-50"
            >
              Reject
            </button>
            <button
              type="button"
              (click)="accept()"
              class="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700"
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    }
  `,
})
export class CookieConsentBannerComponent {
  private readonly consent = inject(CookieConsentService);
  private readonly router = inject(Router);

  // Starts false on both server and client so the first hydrated render
  // matches the server-rendered (bannerless) output exactly; flips true
  // right after that first render settles, which is also the earliest point
  // reading localStorage-derived consent state can't cause a hydration
  // mismatch.
  private readonly ready = signal(false);

  private readonly path = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => currentPath(event.urlAfterRedirects))
    ),
    { initialValue: currentPath(this.router.url) }
  );

  readonly showBanner = computed(
    () => this.ready() && this.consent.status() === 'unset' && isGaTrackedPath(this.path())
  );

  constructor() {
    afterNextRender(() => this.ready.set(true));
  }

  accept(): void {
    this.consent.grant();
  }

  reject(): void {
    this.consent.deny();
  }
}
